import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Plus, Filter } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { useExpenses, useExpenseCategories, useBulkApproveExpenses } from '@/hooks/use-payroll';
import { usePermissions } from '@/hooks/use-permissions';
import {
  SELF_APPROVAL_CONFIRMATION_REQUIRED,
  SelfApprovedBadge,
  approvalErrorMessage,
  useSelfApproval,
} from '@/app/components/payroll/self-approval';
import { PersonSearchComboBox, type PersonOption } from '@/components/shared/person-search-combo-box';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { BulkApproveResult, ExpenseStatus } from '@/lib/types';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  reimbursed: 'bg-purple-100 text-purple-800',
};

export function ExpensesListPage() {
  const orgCurrency = useOrgCurrency();
  const permissions = usePermissions();
  const isManager = permissions.canManagePayroll;

  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [personFilter, setPersonFilter] = useState<PersonOption | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: expenses, isLoading } = useExpenses({
    status: statusFilter || undefined,
    category: categoryFilter ? Number(categoryFilter) : undefined,
    person: isManager ? (personFilter?.person_id ?? undefined) : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });
  const { data: categories } = useExpenseCategories();
  const bulkApprove = useBulkApproveExpenses();
  const selfApproval = useSelfApproval('expense');
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const resultErrors = (results: BulkApproveResult[], skipCode?: string) =>
    results
      .filter((result) => result.error && result.code !== skipCode)
      .map((result) => result.error as string);

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setBulkErrors([]);
    try {
      const { results } = await bulkApprove.mutateAsync({ ids: selectedIds });
      setSelectedIds([]);
      setBulkErrors(resultErrors(results, SELF_APPROVAL_CONFIRMATION_REQUIRED));
      // H3PAY-D9: the approver's own expenses are skipped until they confirm.
      const ownIds = results
        .filter((result) => result.code === SELF_APPROVAL_CONFIRMATION_REQUIRED)
        .map((result) => result.id);
      if (ownIds.length > 0) {
        selfApproval.requestConfirmation('bulk', async () => {
          const confirmed = await bulkApprove.mutateAsync({
            ids: ownIds,
            confirm_self_approval: true,
          });
          setBulkErrors((previous) => [...previous, ...resultErrors(confirmed.results)]);
        });
      }
    } catch (error) {
      setBulkErrors([approvalErrorMessage(error, 'Failed to approve the selected expenses.')]);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600 mt-1">Track and manage expense reports</p>
        </div>
        <div className="flex gap-2">
          {isManager && selectedIds.length > 0 && (
            <Button
              variant="outline"
              onClick={() => void handleBulkApprove()}
              disabled={bulkApprove.isPending}
            >
              Approve Selected ({selectedIds.length})
            </Button>
          )}
          <Button asChild>
            <Link to="/payroll/expenses/new">
              <Plus className="h-4 w-4 mr-2" />
              New Expense
            </Link>
          </Button>
        </div>
      </div>

      {selfApproval.dialog}
      {(bulkErrors.length > 0 || selfApproval.errorFor('bulk')) && (
        <div role="alert" className="text-sm text-red-700 space-y-1">
          {bulkErrors.map((message, index) => (
            <p key={`${index}-${message}`}>{message}</p>
          ))}
          {selfApproval.errorFor('bulk') && <p>{selfApproval.errorFor('bulk')}</p>}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v as ExpenseStatus | '')}>
              <SelectTrigger aria-label="Status filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="reimbursed">Reimbursed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {(categories?.results ?? []).map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isManager && (
              <PersonSearchComboBox
              value={personFilter}
              onChange={(option) => setPersonFilter(option?.person_id != null ? option : null)}
              placeholder="Search workers to filter..."
              endpoint="persons"
              className="mt-0"
            />
            )}
            <Input
              type="date"
              placeholder="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              type="date"
              placeholder="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Expense List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading expenses...</div>
          ) : !(Array.isArray(expenses) ? expenses : []).length ? (
            <div className="p-8 text-center text-gray-500">
              <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No expenses found</p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/payroll/expenses/new">Create your first expense</Link>
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {(Array.isArray(expenses) ? expenses : []).map((expense) => (
                <div
                  key={expense.id}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                >
                  {isManager && (expense.status === 'submitted' || expense.status === 'under_review') && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(expense.id)}
                      onChange={() => toggleSelect(expense.id)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  )}
                  <Link
                    to={`/payroll/expenses/${expense.id}`}
                    className="flex-1 flex items-center justify-between min-w-0"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {expense.description}
                      </p>
                      <p className="text-sm text-gray-500">
                        {expense.person_name ?? expense.tech_name ?? 'Unknown'} &middot; {expense.category_name} &middot; {expense.date}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                      {expense.receipt_count > 0 && (
                        <span className="text-xs text-gray-400">
                          {expense.receipt_count} receipt{expense.receipt_count > 1 ? 's' : ''}
                        </span>
                      )}
                      <span className="font-semibold text-gray-900">
                        {formatMoney(expense.amount, expense.currency ?? orgCurrency)}
                      </span>
                      <Badge className={statusColors[expense.status] ?? 'bg-gray-100 text-gray-800'}>
                        {expense.status.replace('_', ' ')}
                      </Badge>
                      <SelfApprovedBadge selfApproved={expense.self_approved} />
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
